/**
 * Meldet unbehandelte Browser-Fehler an das Backend (`/client-logs`), damit
 * sie in der Admin-Seite „Frontend-Logs" auftauchen. Ohne das bleiben genau
 * die Fehler unsichtbar, die eine Seite im Browser crashen lassen (z.B. ein
 * Temporal-Dead-Zone-Fehler in der Buchungsstrecke) — der Server-Log sieht
 * davon nichts, weil das SSR-HTML sauber ausgeliefert wird.
 *
 * Bewusst schlank: fire-and-forget, dedupliziert im selben Tab, gedrosselt.
 */
const API = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/v1"

const seen = new Set<string>()
let sentCount = 0
const MAX_PER_SESSION = 20

/**
 * Fehler, die nicht von uns stammen und nichts über die Anwendung aussagen.
 *
 * In einer Auswertung über 500 Einträge waren **175** `removeChild`-Fehler durch
 * Browser-Übersetzung oder Erweiterungen, **83** inhaltsleere „Script error." aus
 * fremden Skripten und **30** Aufrufe einer Funktion `uiTrack`, die es in keinem
 * unserer drei Frontends gibt. Zusammen über die Hälfte — und sie haben jede
 * Diagnose ausgebremst.
 */
const FREMDRAUSCHEN: RegExp[] = [
  // React räumt einen Knoten auf, den eine Übersetzung/Erweiterung ersetzt hat.
  /NotFoundError.*(removeChild|insertBefore)/i,
  /(removeChild|insertBefore).*not a child of this node/i,
  // Cross-Origin-Skript ohne CORS-Header: Der Browser liefert keinen Inhalt.
  /^(window\.onerror: )?Script error\.?$/i,
  // Stacks, die ausschließlich aus Erweiterungen kommen.
  /\b(chrome|moz|safari)-extension:\/\//i,
  // Bekannte Kennungen von Erweiterungen und Werbeblockern.
  /\buiTrack is not defined\b/,
]

/**
 * Praefix fuer Meldungen, die wir BEWUSST senden, obwohl sie von einem fremden
 * DOM-Eingriff stammen. Ohne diese Ausnahme wuerde der Filter unten sie schlucken —
 * und wir saehen nicht mehr, ob die Selbstheilung greift.
 */
export const FREMDEINGRIFF_PRAEFIX = "dom-fremdeingriff:"

function istFremdrauschen(message: string): boolean {
  if (message.startsWith(FREMDEINGRIFF_PRAEFIX)) return false
  const kopf = message.slice(0, 400)
  return FREMDRAUSCHEN.some((r) => r.test(kopf))
}

export function reportClientError(message: string, level: "ERROR" | "WARN" = "ERROR"): void {
  if (typeof window === "undefined") return
  try {
    const msg = String(message).slice(0, 4000)
    if (!msg) return
    if (istFremdrauschen(msg)) return
    // Gleichen Fehler pro Tab nur einmal senden (React feuert oft mehrfach).
    const key = msg.slice(0, 200)
    if (seen.has(key)) return
    seen.add(key)
    if (sentCount >= MAX_PER_SESSION) return
    sentCount++

    const body = JSON.stringify({
      level,
      message: msg,
      // Als eigene Felder statt im Meldungstext: Der referer bleibt bei
      // clientseitiger Navigation die Einstiegsseite, taugt also nicht zur Ortsangabe.
      path: `${window.location.pathname}${window.location.search}`.slice(0, 300),
      buildId: process.env.NEXT_PUBLIC_BUILD_ID || undefined,
    })
    // sendBeacon überlebt auch einen unmittelbaren Seitenwechsel/Crash.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/client-logs`, new Blob([body], { type: "application/json" }))
    } else {
      void fetch(`${API}/client-logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => undefined)
    }
  } catch {
    /* Fehler-Reporting darf niemals selbst Fehler werfen */
  }
}

/** Merker, damit aus einem Neuladen keine Schleife wird. */
const RELOAD_KEY = "mk:chunk-reload"

/**
 * Erkennt einen Fehler, der nur davon kommt, dass während der Sitzung neu
 * ausgeliefert wurde: Das geladene HTML verweist auf Chunk-Dateien, die es nach dem
 * Deploy nicht mehr gibt.
 */
export function istVeralteterBuild(message: string): boolean {
  return /module factory is not available|ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
    message,
  )
}

/**
 * Lädt bei einem veralteten Build **einmal** neu und meldet, ob das geschieht.
 *
 * Der Merker in `sessionStorage` ist das Entscheidende: Ohne ihn würde ein Fehler mit
 * ähnlichem Wortlaut, der nicht am Deploy liegt, eine Endlosschleife aus Neuladen
 * erzeugen — schlimmer als der Ausgangsfehler.
 */
export function handleVeralteterBuild(message: string): boolean {
  if (typeof window === "undefined") return false
  if (!istVeralteterBuild(message)) return false
  try {
    if (sessionStorage.getItem(RELOAD_KEY)) return false
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // Ohne sessionStorage (privater Modus) lieber nicht neu laden als endlos.
    return false
  }
  window.location.reload()
  return true
}

/** Nach einem erfolgreichen Seitenaufbau den Merker räumen. */
export function clearVeralteterBuildMerker(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY)
  } catch {
    /* ohne sessionStorage nichts zu tun */
  }
}

/** Merker, damit aus dem Heilungsversuch keine Schleife wird. */
const DOM_EINGRIFF_KEY = "mk:dom-eingriff"

/**
 * Erkennt den Absturz, den ein fremder Eingriff ins DOM auslöst — in aller Regel die
 * Übersetzenfunktion des Browsers, seltener eine Erweiterung.
 *
 * Beide tauschen Textknoten aus, die React selbst eingehängt hat. Räumt React später
 * auf, findet es seinen Knoten nicht mehr und wirft beim `removeChild`.
 *
 * Wichtig ist die erste Zeile: In der Praxis kommt bei WebKit **kein** Fehlertext an,
 * sondern nur der Stack, der mit `removeChild@[native code]` beginnt. Ein Filter, der
 * allein auf `NotFoundError` prüft, greift deshalb in keinem einzigen Fall — genau das
 * war der Grund, warum das Problem 36 Tage lang als „behoben" galt (175 Fälle, davon
 * 149 auf /buchen, ausschließlich iOS/Safari).
 */
export function istFremderDomEingriff(message: string): boolean {
  return (
    /removeChild@\[native code\]/i.test(message) ||
    /NotFoundError.*(removeChild|insertBefore)/i.test(message) ||
    /(removeChild|insertBefore).*not a child of this node/i.test(message)
  )
}

/**
 * Versucht **einmal pro Sitzung**, den Absturz still zu heilen.
 *
 * Bewusst `reset()` statt `location.reload()`: Ein Neuladen würde die Übersetzung
 * erneut anstoßen (also denselben Absturz provozieren) und nebenbei die Eingaben in
 * der Buchungsstrecke verwerfen. `reset()` baut nur den abgestürzten Teilbaum neu auf;
 * der zweite Anlauf läuft fast immer durch, weil die Übersetzung dann bereits fertig ist.
 *
 * Gibt `true` zurück, wenn ein Versuch läuft — dann zeigt der Aufrufer keine
 * Fehlerseite. Ohne `sessionStorage` (privater Modus) wird nicht geheilt: lieber eine
 * ehrliche Fehlerseite als eine Endlosschleife.
 */
export function versucheDomEingriffZuHeilen(reset: () => void): boolean {
  if (typeof window === "undefined") return false
  try {
    if (sessionStorage.getItem(DOM_EINGRIFF_KEY)) return false
    sessionStorage.setItem(DOM_EINGRIFF_KEY, String(Date.now()))
  } catch {
    return false
  }
  reset()
  return true
}
